import { apiClient, ApiResponse } from './apiClient';

// Brand interfaces matching API structure
export interface BrandProduct {
  id: string;
  name: string;
  status: string;
  stock: number;
}

export interface Brand {
  id: string;
  name: string;
  name_ar: string;
  logo_url: string;
  is_active: boolean;
  createdAt: string;
  updatedAt: string;
  Products?: BrandProduct[];
}

export interface BrandDetails extends Brand {
  Products: BrandProduct[];
}

export interface BrandFilters {
  page?: number; // Required, default: 1
  limit?: number; // Required, default: 50, max: 100
  is_active?: string; // Optional: Filter by active status (true/false)
  q?: string; // Optional: Search by name or name_ar
}

export interface BrandPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface BrandsResponse {
  items: Brand[];
  pagination: BrandPagination;
}

export interface BrandListResponse {
  items: Brand[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CreateBrandRequest {
  name: string;
  name_ar: string;
  logo_url: string;
  is_active: boolean;
}

export interface UpdateBrandRequest {
  name?: string;
  name_ar?: string;
  logo_url?: string;
  is_active?: boolean;
}

export class BrandService {
  private baseUrl = '/admin/brands';

  // Get brands with filters (GET /admin/brands)
  async getBrands(filters: BrandFilters = {}): Promise<ApiResponse<BrandListResponse>> {
    const params = new URLSearchParams();
    
    // Required params
    const page = filters.page || 1;
    const limit = Math.min(filters.limit || 50, 100); // Max 100 per API spec
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    // Optional filters
    if (filters.q) {
      params.append('q', filters.q);
    }
    if (filters.is_active !== undefined && filters.is_active !== 'all') {
      params.append('is_active', filters.is_active);
    }
    
    const queryString = params.toString();
    const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;

    try {
      // API returns: {status: boolean, message: string, data: {items: [], pagination: {}}}
      // apiClient.get returns: {success: boolean, data: {...}, message: string}
      const response = await apiClient.get<any>(url);

      // Handle API response structure: {status, message, data: {items, pagination}}
      // apiClient transforms status -> success and wraps the response
      if (response.success && response.data) {
        // response.data contains the API's data object: {items: [], pagination: {}}
        const apiData = response.data;
        const apiItems = apiData.items || [];
        const pagination = apiData.pagination || {
          page: 1,
          limit: 50,
          total: 0,
          totalPages: 0
        };

        const listResponse: BrandListResponse = {
          items: apiItems,
          total: pagination.total,
          page: pagination.page,
          pageSize: pagination.limit,
          totalPages: pagination.totalPages,
        };

        return {
          success: true,
          data: listResponse,
          message: response.message || 'Brands fetched successfully'
        };
      }

      // Return empty response if failed
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: response.message || 'Failed to fetch brands'
      };
    } catch (error: any) {
      console.error('Error fetching brands:', error);
      return {
        success: false,
        data: {
          items: [],
          total: 0,
          page: 1,
          pageSize: limit,
          totalPages: 0
        },
        message: error?.message || 'Error fetching brands'
      };
    }
  }

  // Get brand by ID (GET /admin/brands/{id})
  async getBrandById(id: string): Promise<ApiResponse<BrandDetails>> {
    try {
      const response = await apiClient.get<any>(`${this.baseUrl}/${id}`);
      
      if (response.success && response.data) {
        const apiData = response.data;
        return {
          success: true,
          data: apiData,
          message: response.message || 'Brand fetched successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to fetch brand'
      };
    } catch (error: any) {
      console.error('Error fetching brand:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error fetching brand'
      };
    }
  }

  // Create brand (POST /admin/brands)
  // Upload brand logo (POST /admin/brands/upload-image)
  async uploadImage(file: File, onProgress?: (pct: number) => void): Promise<ApiResponse<{ url: string }>> {
    const formData = new FormData();
    formData.append('image', file);
    return apiClient.upload<{ url: string }>(`${this.baseUrl}/upload-image`, formData, onProgress);
  }

  async createBrand(data: CreateBrandRequest): Promise<ApiResponse<Brand>> {
    try {
      const response = await apiClient.post<Brand>(this.baseUrl, data);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Brand created successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to create brand'
      };
    } catch (error: any) {
      console.error('Error creating brand:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error creating brand'
      };
    }
  }

  // Update brand (PUT /admin/brands/{id})
  async updateBrand(id: string, data: UpdateBrandRequest): Promise<ApiResponse<Brand>> {
    try {
      const response = await apiClient.put<Brand>(`${this.baseUrl}/${id}`, data);
      
      if (response.success && response.data) {
        return {
          success: true,
          data: response.data,
          message: response.message || 'Brand updated successfully'
        };
      }

      return {
        success: false,
        data: undefined,
        message: response.message || 'Failed to update brand'
      };
    } catch (error: any) {
      console.error('Error updating brand:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error updating brand'
      };
    }
  }

  // Delete brand (DELETE /admin/brands/{id})
  async deleteBrand(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      return response;
    } catch (error: any) {
      console.error('Error deleting brand:', error);
      return {
        success: false,
        data: undefined,
        message: error?.message || 'Error deleting brand'
      };
    }
  }

  // Get brand statistics (calculated from brands list)
  async getBrandStats(): Promise<ApiResponse<{
    total: number;
    active: number;
    inactive: number;
  }>> {
    try {
      const response = await this.getBrands({ page: 1, limit: 1000 }); // Get all brands
      if (response.success && response.data) {
        const items = response.data.items;
        const stats = {
          total: items.length,
          active: items.filter(b => b.is_active).length,
          inactive: items.filter(b => !b.is_active).length,
        };
        return { success: true, data: stats, message: 'Stats calculated' };
      }
      throw new Error('Failed to fetch brands for stats');
    } catch (error) {
      // Return default stats if calculation fails
      return {
        success: true,
        data: { total: 0, active: 0, inactive: 0 },
        message: 'Default stats'
      };
    }
  }
}

// Export singleton instance
export const brandService = new BrandService();
export default brandService;

